/** Generic dynamic table helpers — columns discovered from API row objects. */

export interface DynamicDataTableColumn {
  header: string;
  key: string;
}

export interface DynamicDataTableOptions {
  preferredColumnOrder?: string[];
  columnLabels?: Record<string, string>;
  columnFormatters?: Record<string, (value: unknown) => string>;
}

export interface DynamicDataTableResult {
  columnKeys: string[];
  headers: string[];
  columns: DynamicDataTableColumn[];
  rows: string[][];
  rawRows: Record<string, unknown>[];
}

export function getDynamicTableDisplayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? '—' : value;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

export function normalizeDynamicTableKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function isDynamicTableRow(
  value: unknown,
): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function discoverDynamicTableColumns(
  rows: Record<string, unknown>[],
  options?: Pick<DynamicDataTableOptions, 'preferredColumnOrder'>,
): string[] {
  if (!rows.length) {
    return [];
  }

  const ordered: string[] = [];
  const seen = new Set<string>();

  const addKey = (key: string) => {
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    ordered.push(key);
  };

  (options?.preferredColumnOrder ?? []).forEach((key) => {
    if (rows.some((row) => Object.prototype.hasOwnProperty.call(row, key))) {
      addKey(key);
    }
  });

  rows.forEach((row) => {
    Object.keys(row).forEach((key) => addKey(key));
  });

  return ordered;
}

export function buildDynamicDataTable(
  sourceRows: Record<string, unknown>[],
  options?: DynamicDataTableOptions,
): DynamicDataTableResult | null {
  if (!sourceRows.length) {
    return null;
  }

  const columnKeys = discoverDynamicTableColumns(sourceRows, options);
  if (!columnKeys.length) {
    return null;
  }

  const headers = columnKeys.map(
    (key) => options?.columnLabels?.[key] ?? key,
  );
  const columns = columnKeys.map((key, index) => ({
    header: headers[index],
    key,
  }));

  const rows = sourceRows.map((row) =>
    columnKeys.map((key) => {
      const formatter = options?.columnFormatters?.[key];
      const value = row[key];
      return formatter
        ? formatter(value)
        : getDynamicTableDisplayValue(value);
    }),
  );

  return {
    columnKeys,
    headers,
    columns,
    rows,
    rawRows: sourceRows.map((row) => ({ ...row })),
  };
}

const LIQUID_CARGO_ARRAY_ALIASES = new Set([
  'liquidcargo',
  'liquidcargotable',
  'compartmentmetrics',
]);

const LIQUID_CARGO_NESTED_ARRAY_KEYS = ['rows', 'data', 'items', 'table', 'values'];

const LIQUID_CARGO_DIRECT_KEYS = [
  'liquid_cargo',
  'liquidCargo',
  'liquid_cargo_table',
  'liquidCargoTable',
  'compartment_metrics',
  'compartmentMetrics',
];

export function resolveLiquidCargoRows(source: unknown): Record<string, unknown>[] {
  if (!source) {
    return [];
  }

  if (Array.isArray(source)) {
    return source.filter(isDynamicTableRow);
  }

  if (!isDynamicTableRow(source)) {
    return [];
  }

  for (const key of LIQUID_CARGO_DIRECT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const resolved = resolveLiquidCargoRows(source[key]);
      if (resolved.length) {
        return resolved;
      }
    }
  }

  for (const [key, value] of Object.entries(source)) {
    if (!LIQUID_CARGO_ARRAY_ALIASES.has(normalizeDynamicTableKey(key))) {
      continue;
    }
    const resolved = resolveLiquidCargoRows(value);
    if (resolved.length) {
      return resolved;
    }
  }

  for (const value of Object.values(source)) {
    if (!isDynamicTableRow(value)) {
      continue;
    }
    for (const nestedKey of LIQUID_CARGO_NESTED_ARRAY_KEYS) {
      if (Array.isArray(value[nestedKey])) {
        const resolved = resolveLiquidCargoRows(value[nestedKey]);
        if (resolved.length) {
          return resolved;
        }
      }
    }
  }

  return [];
}

export function isNumericDynamicColumn(
  rows: string[][],
  columnIndex: number,
): boolean {
  if (!rows.length) {
    return false;
  }
  let numericCount = 0;
  let checked = 0;
  for (const row of rows) {
    const cell = row[columnIndex];
    if (cell === '—' || cell.trim() === '') {
      continue;
    }
    checked += 1;
    if (!Number.isNaN(Number(String(cell).replace(/,/g, '')))) {
      numericCount += 1;
    }
  }
  return checked > 0 && numericCount / checked >= 0.6;
}
